/**
 * Wave 3 Block 3A — CommentForm
 * 
 * Form for adding comments to authorization timeline.
 * Supports different comment types and attachments.
 * 
 * Item 3a: STS Scenarios History
 */
import { useState } from 'react';
import { Send, FileText, MessageSquare, Paperclip, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import api from '../../lib/api';

const COMMENT_TYPES = [
  { value: 'internal_note', label: { cs: 'Interní poznámka', en: 'Internal Note' }, icon: FileText },
  { value: 'ucl_feedback', label: { cs: 'Zpětná vazba ÚCL', en: 'ÚCL Feedback' }, icon: MessageSquare },
  { value: 'operator_response', label: { cs: 'Odpověď operátora', en: 'Operator Response' }, icon: Send }
];

export const CommentForm = ({ 
  authorizationId, 
  language = 'cs',
  onCommentAdded,
  disabled = false
}) => {
  const [comment, setComment] = useState('');
  const [commentType, setCommentType] = useState('internal_note');
  const [submitting, setSubmitting] = useState(false);
  
  const t = {
    placeholder: language === 'cs' 
      ? 'Napište komentář nebo poznámku...' 
      : 'Write a comment or note...',
    submit: language === 'cs' ? 'Přidat komentář' : 'Add Comment',
    submitting: language === 'cs' ? 'Ukládám...' : 'Saving...',
    success: language === 'cs' ? 'Komentář přidán' : 'Comment added',
    error: language === 'cs' ? 'Chyba při přidávání komentáře' : 'Error adding comment',
    selectType: language === 'cs' ? 'Typ komentáře' : 'Comment Type'
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) return;
    
    setSubmitting(true);
    
    try {
      await api.post(`/authorizations/${authorizationId}/revisions`, {
        comment_type: commentType,
        comment: comment.trim(),
        attachments: []
      });
      
      toast.success(t.success);
      setComment('');
      onCommentAdded?.();
    } catch (err) {
      console.error('Failed to add comment:', err);
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const selectedType = COMMENT_TYPES.find(t => t.value === commentType);
  const TypeIcon = selectedType?.icon || FileText;
  
  return (
    <Card data-testid="comment-form">
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <Select value={commentType} onValueChange={setCommentType} disabled={disabled}>
              <SelectTrigger className="w-48">
                <div className="flex items-center gap-2">
                  <TypeIcon className="w-4 h-4" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {COMMENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label[language]}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t.placeholder}
            disabled={disabled || submitting}
            rows={3}
            className="resize-none"
            data-testid="comment-textarea"
          />
          
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled
              className="text-gray-400"
            >
              <Paperclip className="w-4 h-4 mr-1" />
              {language === 'cs' ? 'Příloha' : 'Attach'}
            </Button>
            
            <Button
              type="submit"
              disabled={disabled || submitting || !comment.trim()}
              size="sm"
              data-testid="submit-comment-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  {t.submitting}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1" />
                  {t.submit}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CommentForm;
